import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { CheckCircle2, Phone, Activity, Brain, Accessibility, Heart, Stethoscope, HandHeart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import VideoBackground from '../components/VideoBackground';
import SEO from '../components/SEO';

const TherapiesNdisPage = () => {
  const faqs = [
    {
      question: "What types of therapy does the NDIS fund?",
      answer: "The NDIS funds a range of therapies including occupational therapy, physiotherapy, speech therapy, psychology, behavioural therapy, music therapy, art therapy, and exercise physiology. These are typically funded under Capacity Building — Improved Daily Living."
    },
    {
      question: "How do I know which therapy is right for me?",
      answer: "Our team conducts thorough assessments to understand your needs, goals, and preferences. We then recommend therapies that align with your NDIS plan and will have the greatest positive impact on your daily life and independence."
    },
    {
      question: "Can I access multiple therapies at the same time?",
      answer: "Yes, many participants benefit from a combination of therapies. For example, you might attend occupational therapy for daily living skills alongside physiotherapy for mobility. Your plan funding determines which therapies you can access concurrently."
    },
    {
      question: "How are therapy goals set?",
      answer: "Goals are set collaboratively between you, your therapist, and your support team. We use a strengths-based approach, focusing on what you want to achieve and creating measurable milestones to track your progress."
    },
    {
      question: "Can therapy be delivered at home or in the community?",
      answer: "Yes, many of our therapy services can be delivered in your home, at community locations, or via telehealth. We offer flexible delivery options to ensure therapy fits into your life and is accessible regardless of your circumstances."
    },
    {
      question: "How often will I need therapy sessions?",
      answer: "Session frequency depends on your individual needs and NDIS plan funding. Your therapist will recommend a schedule that supports your goals — this might be weekly, fortnightly, or as-needed. Frequency can be adjusted as you progress."
    }
  ];

  const scrollToContact = () => { window.location.href = '/contact'; };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Therapies On The NDIS" description="Evidence-based NDIS therapeutic services in Brisbane. Occupational therapy, physiotherapy, psychology, speech therapy, music therapy and more." />
      <Header />

      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#587740' }} data-testid="therapies-ndis-title">
                Therapies On The NDIS
              </h1>
              <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                The right therapy can be life-changing — but only if it's delivered by people who truly care about your progress. At Life Care and Wellness, our therapists don't just follow a textbook. They take the time to understand you, your body, your mind, and what you want to achieve.
              </p>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                In plain language — whether you need help with movement and strength, managing emotions, communicating more clearly, or building everyday skills, we design a therapy plan that actually fits your life. All funded through your NDIS plan.
              </p>
              <p className="text-base leading-relaxed italic mb-4" style={{ color: '#6c935e' }}>
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p className="text-sm font-semibold mb-8" style={{ color: '#587740' }}>
                Free consultation and personalised action plan — let's talk about the therapy that's right for you.
              </p>
              <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }} data-testid="therapies-hero-cta">
                <Phone className="w-5 h-5 mr-2" /> Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={images.whyChoose} alt="Therapeutic support services" className="w-full h-[400px] md:h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: '#587740' }}>
            Therapeutic Support for Your Goals
          </h2>
          <p className="text-lg text-center max-w-4xl mx-auto mb-12" style={{ color: '#5e5e5e' }}>
            The NDIS funds therapeutic supports that help you build your abilities, improve how your body and mind function, and move towards greater independence. Put simply — therapy is about giving you the tools to do more of what matters to you, at your own pace.
          </p>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.personalTraining} alt="Physical therapy session" className="w-full h-[350px] object-cover" />
            </div>
            <div>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                From physical therapies that help you move better and feel stronger, to psychological supports that help you manage difficult emotions — we look after the whole person, not just one part. Unlike providers who stick to rigid programs, we adjust our approach based on what's actually working for you.
              </p>
              <ul className="space-y-3">
                {["Person-centred and goal-oriented approach", "Qualified and experienced therapists", "Flexible delivery — clinic, home, or telehealth", "Regular progress reviews and plan adjustments", "Coordination with your wider support team"].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                    <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Therapy Services We Provide
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Accessibility, title: "Occupational Therapy", desc: "Helping you develop skills for daily living, from self-care and household tasks to workplace adaptations and assistive technology." },
              { icon: Activity, title: "Physiotherapy", desc: "Improving mobility, strength, and physical function through tailored exercise programs, manual therapy, and movement strategies." },
              { icon: Brain, title: "Psychology & Counselling", desc: "Supporting your mental health and emotional well-being through evidence-based therapies including CBT, mindfulness, and behavioural approaches." },
              { icon: Stethoscope, title: "Speech Therapy", desc: "Enhancing communication skills, from speech and language development to swallowing support and alternative communication methods." },
              { icon: Heart, title: "Music & Art Therapy", desc: "Creative therapeutic approaches that use music and art to support emotional expression, social skills, and cognitive development." },
              { icon: HandHeart, title: "Positive Behaviour Support", desc: "Developing strategies to address challenging behaviours, improve quality of life, and build positive skills in a supportive framework." }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#AABC5A' }}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: '#587740' }}>{item.title}</h3>
                    <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#587740' }}>
                Our Approach to Therapy
              </h2>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                We take a holistic approach to therapy, recognising that your well-being involves physical, emotional, and social dimensions. Our therapists work together as a team to provide coordinated care that addresses all aspects of your health.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#5e5e5e' }}>
                Every therapy plan begins with understanding your goals and aspirations. We then design a program that builds your capacity step by step, celebrating your achievements along the way and adapting as your needs evolve.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.supportCoordinator2} alt="Holistic therapy approach" className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }} data-testid="therapies-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg px-6 shadow-sm bg-white" data-testid={`therapies-faq-${i}`}>
                <AccordionTrigger className="text-left hover:no-underline py-6" style={{ color: '#587740' }}>
                  <span className="text-lg font-semibold pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: '#5e5e5e' }}>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }}>
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>

      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Your Well-Being Matters — Let's Build a Plan Together</h2>
          <p className="text-lg mb-8 text-white/90">Our qualified therapists are here to help you feel stronger, more capable, and more confident. Free consultation, free action plan — because great care shouldn't start with a bill.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+611300404229">
            <Button size="lg" className="text-base px-8 py-6" style={{ backgroundColor: '#FFFFFF', color: '#587740' }}>
              <Phone className="w-5 h-5 mr-2" /> Call Us Now
            </Button>
          </a>
          <a href="/get-on-ndis-free">
            <Button size="lg" variant="outline" className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10">
              Get on the NDIS Free
            </Button>
          </a>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default TherapiesNdisPage;
